import { useState, useRef, useEffect } from "react";
import { MagnifierWindow } from "./MagnifierWindow";

// Mock data structure - easily replaceable
export interface MinimapEvent {
  id: string;
  type: 'message' | 'system' | 'join' | 'leave' | 'typing' | 'reaction';
  timestamp: number;
  content?: string;
  participantId?: string;
  participantName?: string;
}

// TODO: Replace with actual conversation/thread data
const MOCK_EVENTS: MinimapEvent[] = Array.from({ length: 50 }, (_, i) => ({
  id: `event-${i}`,
  type: ['message', 'system', 'join', 'leave', 'typing', 'reaction'][Math.floor(Math.random() * 6)] as MinimapEvent['type'],
  timestamp: Date.now() - (50 - i) * 60000,
  content: `Event ${i} content`,
  participantId: `participant-${Math.floor(Math.random() * 5)}`,
  participantName: `User ${Math.floor(Math.random() * 5)}`,
}));

const EVENT_COLORS: Record<MinimapEvent['type'], string> = {
  message: 'bg-blue-500',
  system: 'bg-gray-500',
  join: 'bg-green-500',
  leave: 'bg-red-500',
  typing: 'bg-yellow-500',
  reaction: 'bg-purple-500',
};

const DEFAULT_MINIMAP_WIDTH = 50;
const MIN_MINIMAP_WIDTH = 30;
const MAX_MINIMAP_WIDTH = 150;

interface MinimapViewProps {
  side: 'left' | 'right';
  onSideChange: (side: 'left' | 'right') => void;
}

export function MinimapView({ side, onSideChange }: MinimapViewProps) {
  const [width, setWidth] = useState(DEFAULT_MINIMAP_WIDTH);
  const [isResizing, setIsResizing] = useState(false);
  const [hoverPosition, setHoverPosition] = useState<{ x: number; y: number } | null>(null);
  const [hoverEventIndex, setHoverEventIndex] = useState<number | null>(null);
  const minimapRef = useRef<HTMLDivElement>(null);

  // Handle resize
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  };

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!minimapRef.current) return;

      const rect = minimapRef.current.getBoundingClientRect();
      let newWidth: number;

      if (side === 'left') {
        newWidth = e.clientX - rect.left;
      } else {
        newWidth = rect.right - e.clientX;
      }

      newWidth = Math.max(MIN_MINIMAP_WIDTH, Math.min(MAX_MINIMAP_WIDTH, newWidth));
      setWidth(newWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, side]);

  // Handle hover for magnifier
  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!minimapRef.current) return;

    const rect = minimapRef.current.getBoundingClientRect();
    const relativeY = e.clientY - rect.top;
    const eventIndex = Math.floor((relativeY / rect.height) * MOCK_EVENTS.length);

    setHoverPosition({ x: e.clientX, y: e.clientY });
    setHoverEventIndex(Math.max(0, Math.min(MOCK_EVENTS.length - 1, eventIndex)));
  };

  const handleMouseLeave = () => {
    setHoverPosition(null);
    setHoverEventIndex(null);
  };

  return (
    <>
      <div
        ref={minimapRef}
        className={`relative flex-shrink-0 bg-gray-100 dark:bg-gray-900 border-gray-300 dark:border-gray-600 flex flex-col ${
          side === 'left' ? 'border-r' : 'border-l'
        }`}
        style={{ 
          width: `${width}px`,
          margin: '0.125rem',
          padding: '0.125rem',
        }}
      >
        {/* Header Controls */}
        <div className="flex items-center justify-center mb-1 px-1 flex-shrink-0 h-5">
          <button
            onClick={() => onSideChange(side === 'left' ? 'right' : 'left')}
            className="text-[10px] px-1 py-0.5 hover:bg-gray-200 dark:hover:bg-gray-700 rounded"
            title="Switch side"
          >
            ⇄
          </button>
        </div>

        {/* Minimap Content - Event Blocks */}
        <div 
          className="flex-1 flex flex-col gap-[1px] overflow-hidden min-h-0"
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
        >
          {MOCK_EVENTS.map((event, index) => (
            <div
              key={event.id}
              className={`h-2 ${EVENT_COLORS[event.type]} opacity-80 hover:opacity-100 transition-opacity`}
              title={`${event.type} - ${new Date(event.timestamp).toLocaleTimeString()}`}
              style={{
                height: `${100 / MOCK_EVENTS.length}%`,
              }}
            />
          ))}
        </div>

        {/* Resize Handle */}
        <div
          className={`absolute top-0 bottom-0 w-1 cursor-col-resize hover:bg-blue-500 transition-colors ${
            side === 'left' ? 'right-0' : 'left-0'
          } ${isResizing ? 'bg-blue-500' : ''}`}
          onMouseDown={handleMouseDown}
          style={{
            [side === 'left' ? 'right' : 'left']: '-2px',
            width: '4px',
          }}
        />
      </div>

      {/* Magnifier Window */}
      {hoverPosition && hoverEventIndex !== null && (
        <MagnifierWindow
          position={hoverPosition}
          events={MOCK_EVENTS}
          centerEventIndex={hoverEventIndex}
          side={side}
        />
      )}
    </>
  );
}

